import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Stars } from "@/components/Stars";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function Review() {
  const [params] = useSearchParams();
  const bookingId = params.get("b");
  const [booking, setBooking] = useState(null);
  const [form, setForm] = useState({ name: "", rating: 0, text: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!bookingId) return;
    api.get(`/reviews/booking/${bookingId}`).then(({ data }) => {
      setBooking(data);
      setForm((f) => ({ ...f, name: `${data.first_name} ${data.last_initial ? data.last_initial + "." : ""}`.trim() }));
    }).catch(() => {});
  }, [bookingId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return toast.error("Please choose a star rating");
    if (form.text.trim().length < 10) return toast.error("Please write at least a few words");
    setBusy(true);
    try {
      await api.post("/reviews", { ...form, booking_id: booking ? bookingId : null });
      setDone(true);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="pt-28 pb-24 max-w-xl mx-auto px-5" data-testid="review-page">
      <p className="font-mono-label uppercase tracking-[0.3em] text-xs text-[#D4AF37]">Your Experience</p>
      <h1 className="font-display text-4xl sm:text-5xl text-white mt-3 mb-3">Leave a Review</h1>
      {booking && <p className="text-white/50 text-sm mb-8" data-testid="review-booking-info">For your <span className="text-white">{booking.service_title}</span> session</p>}
      {done ? (
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#111] border border-[#D4AF37]/30 rounded-2xl p-8 text-center mt-8" data-testid="review-success">
          <CheckCircle2 className="mx-auto text-[#D4AF37]" size={40} />
          <h2 className="font-display text-2xl text-white mt-4">Thank you!</h2>
          <p className="text-white/60 text-sm mt-2">Your review will appear on the site once our team approves it.</p>
          <Link to="/" className="btn-gold inline-flex rounded-full px-8 py-3 mt-6 text-sm font-mono-label uppercase" data-testid="review-home-btn">Back to Home</Link>
        </motion.div>
      ) : (
        <form onSubmit={submit} className="bg-[#111] border border-white/10 rounded-2xl p-6 md:p-8 space-y-5 mt-8" data-testid="review-form">
          <div>
            <label className="text-white/60 text-xs font-mono-label uppercase tracking-wide">Rating</label>
            <div className="mt-2"><Stars value={form.rating} size={30} onChange={(rating) => setForm({ ...form, rating })} testid="review-rating" /></div>
          </div>
          <div>
            <label className="text-white/60 text-xs font-mono-label uppercase tracking-wide">Your name</label>
            <Input required maxLength={80} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-[#0a0a0a] border-white/15" placeholder="e.g. Jordan M." data-testid="review-name" />
          </div>
          <div>
            <label className="text-white/60 text-xs font-mono-label uppercase tracking-wide">Your review</label>
            <Textarea required maxLength={1500} rows={5} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="mt-2 bg-[#0a0a0a] border-white/15" placeholder="Tell us about your session…" data-testid="review-text" />
          </div>
          <button disabled={busy} className="btn-gold w-full rounded-full py-3 text-sm font-mono-label uppercase disabled:opacity-60" data-testid="review-submit">
            {busy ? "Submitting…" : "Submit Review"}
          </button>
        </form>
      )}
    </div>
  );
}
